import api from './api';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  is_crm_user: boolean;
  current_company: {
    id: number;
    name: string;
    slug: string;
  } | null;
  company_count: number;
  date_joined: string;
  last_login: string | null;
}

export interface CompanyMembership {
  id: number;
  user: number;
  company: {
    id: number;
    name: string;
    slug: string;
  };
  role: string;
  role_display: string;
  is_active: boolean;
  joined_at: string;
  can_manage_students: boolean;
  can_view_reports: boolean;
  can_manage_users: boolean;
}

export interface Company {
  id: number;
  name: string;
  slug: string;
  city: string;
}

export interface UserFilters {
  search?: string;
}

export interface UserResponse {
  success: boolean;
  data?: User[];
  message?: string;
}

export interface UserDetailResponse {
  success: boolean;
  data?: User;
  message?: string;
}

export interface MembershipResponse {
  success: boolean;
  data?: CompanyMembership[];
  message?: string;
}

export interface CompanyResponse {
  success: boolean;
  data?: Company[];
  message?: string;
}

export const userManagementAPI = {
  // İstifadəçilərin siyahısını əldə et
  getUsers: async (filters?: UserFilters): Promise<UserResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);

      const response = await api.get(`/auth/user-management/?${params.toString()}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'İstifadəçilər yüklənmədi'
      };
    }
  },

  // İstifadəçi məlumatlarını əldə et
  getUser: async (userId: number): Promise<UserDetailResponse> => {
    try {
      const response = await api.get(`/auth/user-management/${userId}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'İstifadəçi məlumatları yüklənmədi'
      };
    }
  },

  // İstifadəçinin şirkət üzvlüklərini əldə et
  getUserMemberships: async (userId: number): Promise<MembershipResponse> => {
    try {
      const response = await api.get(`/auth/user-management/${userId}/memberships/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Üzvlüklər yüklənmədi'
      };
    }
  },

  // İstifadəçiyə yeni şirkət üzvlüyü əlavə et
  addMembership: async (userId: number, companyId: number, role: string): Promise<{ success: boolean; message?: string; data?: CompanyMembership }> => {
    try {
      const response = await api.post(`/auth/user-management/${userId}/add_membership/`, {
        company_id: companyId,
        role: role
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Üzvlük əlavə edilmədi'
      };
    }
  },

  // İstifadəçinin şirkət üzvlüyünü yenilə
  updateMembership: async (userId: number, membershipId: number, data: Partial<CompanyMembership>): Promise<{ success: boolean; message?: string; data?: CompanyMembership }> => {
    try {
      const response = await api.patch(`/auth/user-management/${userId}/update_membership/`, {
        membership_id: membershipId,
        ...data
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Üzvlük yenilənmədi'
      };
    }
  },

  // İstifadəçini şirkətdən çıxar
  removeMembership: async (userId: number, membershipId: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/auth/user-management/${userId}/remove_membership/`, {
        data: { membership_id: membershipId }
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Üzvlük silinmədi'
      };
    }
  },

  // Mövcud şirkətlərin siyahısını əldə et
  getAvailableCompanies: async (): Promise<CompanyResponse> => {
    try {
      const response = await api.get('/auth/user-management/available_companies/');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkətlər yüklənmədi'
      };
    }
  },

  // Yeni istifadəçi yarat
  createUser: async (userData: Partial<User> & { password: string }): Promise<{ success: boolean; message?: string; data?: User; errors?: any }> => {
    try {
      const response = await api.post('/auth/user-management/', userData);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'İstifadəçi yaradılmadı',
        errors: error.response?.data?.errors
      };
    }
  },

  // İstifadəçi məlumatlarını yenilə
  updateUser: async (userId: number, userData: Partial<User>): Promise<{ success: boolean; message?: string; data?: User; errors?: any }> => {
    try {
      const response = await api.patch(`/auth/user-management/${userId}/`, userData);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'İstifadəçi yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // İstifadəçini sil
  deleteUser: async (userId: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/auth/user-management/${userId}/`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'İstifadəçi silinmədi'
      };
    }
  }
};